import React, { createContext, useContext, useMemo, useState } from "react"
import { useMemoizedFn } from "../hooks/use-memoized-fn"

type PortalLayerContextType = {
  container: HTMLElement
  registerAnchor: (element: HTMLElement) => void
}

const PortalLayers = createContext<PortalLayerContextType>({
  container: document.body,
  registerAnchor: () => console.error("Cannot set inner element for PortalLayers: context is null"),
})

/**
 * Define this element as a new portal layer. Indented to solve z-index issues
 * with nested portals.
 * <p>
 * Wrapped component should accept DOM data attributes. If this is not possible,
 * `data-portal-layer` needs to be added onto a DOM element wrapping the portal contents.
 * <p>
 * Child portals (e.g. dropdowns, popovers, dialogs) should have this layer's
 * container element as parent in the DOM by using {@link usePortalContainer()}
 * <p>
 * Requires an anchor to actually register the container element as portal layer:
 * @see createPortalAnchor
 * @see PortalHiddenAnchor
 */
export function createPortalLayer<C extends React.ElementType>(
  PrimitiveRoot: C
) {
  return function WithProvider(props: React.ComponentProps<C>) {
    const Root = PrimitiveRoot as React.ElementType
    return (
      <PortalLayerProvider>
        <Root {...props} data-portal-layer />
      </PortalLayerProvider>
    )
  }
}

/**
 * Wraps a component to register its DOM element as the anchor for the closest portal layer.
 * <p>
 * **Important**: the wrapped component must forward its ref to a DOM element
 */
export function createPortalAnchor<
  Component extends React.ElementType,
  RefElem extends HTMLElement = React.ElementRef<Component> & HTMLElement,
>(
  PrimitiveTrigger: Component
) {
  type Props = React.ComponentPropsWithoutRef<Component>

  return React.forwardRef<RefElem, Props>((props, forwardedRef) => {
    const setPopoverTriggerElement = useContext(PortalLayers).registerAnchor

    const setRef = useMemoizedFn((element: RefElem) => {
      setPopoverTriggerElement(element)

      if (typeof forwardedRef === "function") {
        forwardedRef(element)
      } else if (forwardedRef) {
        forwardedRef.current = element
      }
    })

    const Trigger = PrimitiveTrigger as React.ElementType
    return <Trigger {...props} ref={setRef} />
  })
}

/** Hidden anchor helper in case there is no "natural" element to use */
export const PortalHiddenAnchor = createPortalAnchor(
  React.forwardRef<HTMLDivElement>(function PortalHiddenAnchorInner(_unused, ref) {
    return <div ref={ref} className="hidden" />
  })
)

/**
 * Returns nearest portal layer container (if there is no, it returns `document.body`)
 */
export function usePortalContainer() {
  return useContext(PortalLayers).container
}

function PortalLayerProvider(props: {
  children: React.ReactNode
}) {
  const parentContext = useContext(PortalLayers)
  const defaultParent = parentContext.container ?? document.body
  const [ parent, setParent ] = useState<HTMLElement>(defaultParent)
  const context = useMemo<PortalLayerContextType>(() => {
    return {
      container: parent,
      registerAnchor: (innerElement) => setParent(findNearestPortalTarget(innerElement, defaultParent)),
    }
  }, [ parent, defaultParent ])
  return (
    <PortalLayers.Provider value={context}>
      {props.children}
    </PortalLayers.Provider>
  )
}

export function findOutermostPortalTarget(element: HTMLElement): HTMLElement {
  // in document order, so the first layer containing the element is the outermost
  const layers = document.querySelectorAll<HTMLElement>('[data-portal-layer]')
  return [ ...layers ].find(layer => layer.contains(element)) ?? document.body
}

function findNearestPortalTarget(innerElement: HTMLElement | null, defaultParent = document.body): HTMLElement {
  if (!innerElement) return defaultParent

  const closestModalRoot = innerElement.closest('[data-portal-layer]') as HTMLElement | null

  if (closestModalRoot) return closestModalRoot
  return defaultParent
}
